import { bcbs_config_tierlevel } from "model/CRM";
import { IAccountsReceivable, IBenefit, IFeesAndPremiums, IFlexibleField, IFlexibleFieldSet, IGroupConfigurationConfig, IGroupConfigurationData, IGroupNumber, INonStandard, IPlan, IRef, IStopLoss, ITierLevel } from "model/FlexibleAttributeModel";
import { blankSnapshot } from "model/snapshotTemplate";
import { FlexibleFieldGenerator } from "../../model/FlexibleFieldGenerator";
import { BaseJsonTransformer, ETransformContextType, IBenefitTransformContext, ITransformContext } from "./IJsonTransformer";
import { v4 as uuidv4 } from 'uuid';
/**
 * Updates attribute sort order and adds in any missing attributes given by the release.
 * NOTE: If fields no longer exist (which shouldn't happen), the fields will be appended
 * to the end of the field array (ensuring no data loss).
 */
export class UpdateAttributesTransformer extends BaseJsonTransformer {
    readonly release: IGroupConfigurationConfig;
    readonly generator: FlexibleFieldGenerator;

    readonly latestIteration: number;
    jsonIteration: number;

    constructor(release: IGroupConfigurationConfig) {
        super();

        this.release = release;
        this.generator = new FlexibleFieldGenerator(this.release);

        this.latestIteration = blankSnapshot().iteration;
        this.jsonIteration = -1;
    }

    async before(json: IGroupConfigurationData): Promise<void> {
        console.log("[tran] UpdateAttributesTransformer.before");
        this.jsonIteration = json.iteration;
    }

    async transformGroupConfiguration(json: IGroupConfigurationData): Promise<void> {
        let templateGroupConfigurationFields = this.generator.generateGroupConfigurationFields();
        this.processFields(json.fields, templateGroupConfigurationFields);
    }

    async transformAttributeSet(attributeSet: IFlexibleFieldSet, context: ITransformContext): Promise<boolean | void> {
        let templateAttributeSetFields = this.generator.generateAttributeSetFields(attributeSet.attributeSetRef.id);
        this.processFields(attributeSet.fields, templateAttributeSetFields);
    }

    async transformNonStandard(nonStandard: INonStandard, context: ITransformContext): Promise<boolean | void> {
        const tiers = context.type === ETransformContextType.Benefit
            ? (context as IBenefitTransformContext).parent.tiers ?? []
            : undefined;

        if (nonStandard.sectionRef && nonStandard.sectionRef.id) {
            let templateNonStandardFields = this.generator.generateNonStandardFields(nonStandard.sectionRef.id, tiers);
            this.processFields(nonStandard.fields, templateNonStandardFields, tiers);
        }
    }

    async transformBenefit(benefit: IBenefit): Promise<boolean | void> {
        if (benefit.tierSetRef && benefit.tiers && benefit.tiers.length > 0) {
            const templateLevels = this.release.cache.map_bcbs_config_tierlevels_by_tierSetId[benefit.tierSetRef.id];
            this.processTiers(benefit, templateLevels);
        }

        let templateBenefitFields = this.generator.generateBenefitFields(benefit.bcbs_config_categoryid!, benefit.tiers);
        this.processFields(benefit.fields, templateBenefitFields, benefit.tiers);
    }


    async transformAccountsReceivable(accountsReceivable: IAccountsReceivable): Promise<boolean | void> {
        let templateAccountsReceivablesFields = this.generator.generateAccountsReceivablesFields();
        this.processFields(accountsReceivable.fields, templateAccountsReceivablesFields);
    }

    async transformStopLoss(stopLoss: IStopLoss): Promise<boolean | void> {
        let templateStopLossFields = this.generator.generateStopLossFields();
        this.processFields(stopLoss.fields, templateStopLossFields);
    }

    async transformFeesAndPremium(feesAndPremium: IFeesAndPremiums): Promise<boolean | void> {
        let templateFeesAndPremiumsFields = this.generator.generateFeesAndPremiumsFields();
        this.processFields(feesAndPremium.fields, templateFeesAndPremiumsFields);
    }

    async transformPlan(plan: IPlan): Promise<boolean | void> {
        let templatePlanFields = this.generator.generatePlanFields();
        this.processFields(plan.fields, templatePlanFields);
    }

    async transformGroupNumber(groupNumber: IGroupNumber): Promise<boolean | void> {
        let templateGroupNumberFields = this.generator.generateGroupNumberFields();
        this.processFields(groupNumber.fields, templateGroupNumberFields);
    }

    processTiers(benefit: IBenefit, templateLevels: bcbs_config_tierlevel[]) {

        const sourceLevels = benefit.tiers;
        const tierSetRef = benefit.tierSetRef;

        // repair tiers if it does not exist
        let tierMap = new Map<string, ITierLevel>();
        sourceLevels.forEach(tier => {
            tierMap.set(tier.bcbs_name!, tier);
        });
        sourceLevels.length = 0;

        for (let i = 0; i < templateLevels.length; i++) {
            let templateLevel = templateLevels[i];
            const existingLevel = tierMap.get(templateLevel.bcbs_name!);

            tierMap.delete(templateLevel.bcbs_name!);

            if (existingLevel)
                sourceLevels[i] = existingLevel;
            else {
                console.warn(`Tier information missing for tier set (${tierSetRef?.name}/${tierSetRef?.id}).`);

                if (this.jsonIteration === 0) {
                    // self Repair a missing tier level
                    sourceLevels[i] = {
                        ...templateLevel!,
                        name: templateLevel.bcbs_name!,
                        // TODO find the missing id and use that instead
                        id: uuidv4(),
                        description: '',
                        pcp: false,
                        ion: false,
                        isLabelDisabled: false
                    };
                    const matchingField = benefit.fields.find(q => q.values.find(w => w.tierRef?.name === templateLevel.bcbs_name));
                    const matchingFieldValue = matchingField?.values?.find(q => q.tierRef?.name === templateLevel.bcbs_name);

                    if (matchingFieldValue) {
                        sourceLevels[i].id = matchingFieldValue?.tierRef?.id!;
                        console.warn("Repairing has been attempted with defaults.");
                    }
                } else {
                    throw new Error(`Tier information missing for tier set (${tierSetRef?.name}/${tierSetRef?.id}).`);
                }

            }

        }

    }


    processFields(sourceFields: IFlexibleField[], templateFields: IFlexibleField[], tiers?: ITierLevel[]): void {

        // build a map of config ref to source field
        let fieldMap = new Map<string, IFlexibleField>();
        sourceFields.forEach(field => {
            fieldMap.set(field.configRef.id, field);
        });

        // now iterate the template fields, replacing indexes with either something from the
        // map or something from the template
        sourceFields.length = 0;
        for (let i = 0; i < templateFields.length; i++) {
            let templateField = templateFields[i];
            let existingField = fieldMap.get(templateField.configRef.id);

            // remove it from the map to indicate that we've put the field back
            // into the array
            fieldMap.delete(templateField.configRef.id);

            if (existingField) {

                if (tiers && tiers.length > 1) {
                    sourceFields[i] = { ...existingField, values: templateField.values };

                    // to guarantee order and existence, has to be reconstructed from 
                    for (let j = 0; j < sourceFields[i].values.length; j++) {
                        const sourceValue = sourceFields[i].values[j];

                        // note: template is generated with same tiers, so refs should be the sharing same id
                        const iValue = existingField?.values
                            .find(q => q.id === sourceValue.id || (q.tierRef?.id === sourceValue.tierRef?.id && q.type === sourceValue.type));

                        // replace if we found a matching value
                        if (iValue)
                            sourceFields[i].values[j] = iValue;
                    }

                }
                else {
                    sourceFields[i] = existingField;
                }
            } else {
                sourceFields[i] = templateField;
            }
        }

        // NOTE: this if was put in place during development to remove fields
        // generated incorrectly during a deployment
        if (this.jsonIteration <= 4) {
            return;
        }

        // now add back anything that wasn't added based on the template
        // we do this to ensure that field data isn't lost in the event of
        // misconfigured UI layout
        fieldMap.forEach(field => {
            console.warn(`UpdateAttributesTransformer: Field (${field.configRef.name})/${field.configRef.id}/${field.visibility} was not part of the given release.`)

            sourceFields.push(field);
        });
    }
}
